"use client"

import { useState, useEffect } from "react"
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi"
import { getGateways, createGateway, updateGateway, deleteGateway } from "../utils/api"

export default function PaymentGateway() {
  // Payment gateways data
  const [gateways, setGateways] = useState([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingGateway, setEditingGateway] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    features: [""],
    link: "",
    isActive: true
  })

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null)

  // Fetch gateways on mount
  useEffect(() => {
    fetchGateways()
  }, [])

  const fetchGateways = async () => {
    try {
      const data = await getGateways()
      setGateways(data)
    } catch (err) {
      console.error(err)
    }
  }

  // Handle all input changes
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: inputType === "checkbox" ? checked : value,
    }))
  }

  // Features array handling
  const handleFeatureChange = (idx, value) => {
    setFormData((prev) => {
      const features = [...prev.features]
      features[idx] = value
      return { ...prev, features }
    })
  }

  const addFeature = () => setFormData((prev) => ({ ...prev, features: [...prev.features, ""] }))

  const removeFeature = (idx) =>
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== idx),
    }))

  // Submit create or update
  const handleSubmit = async (e) => {
    e.preventDefault()
    const { name, features, link, isActive } = formData

    if (!name || !features || features.length === 0) {
      alert("Please fill in name and at least one feature")
      return
    }

    const payload = {
      name,
      features: features.filter((f) => f.trim()),
      ...(link && { link }),
      isActive
    }

    try {
      if (editingGateway?._id) {
        await updateGateway(editingGateway._id, payload)
      } else {
        await createGateway(payload)
      }
      await fetchGateways()
      closeDialog()
    } catch (err) {
      console.error(err)
      alert(err.message || "Failed to save payment gateway")
    }
  }

  // Open form for edit
  const handleEdit = (gateway) => {
    setEditingGateway(gateway)
    setFormData({
      name: gateway.name,
      features: gateway.features.length ? gateway.features : [""],
      link: gateway.link || "",
      isActive: gateway.isActive
    })
    setIsDialogOpen(true)
  }

  // Delete flow
  const confirmDelete = (id) => setDeleteTarget(id)
  const handleConfirmDelete = async () => {
    try {
      await deleteGateway(deleteTarget)
      await fetchGateways()
    } catch (err) {
      console.error(err)
    } finally {
      setDeleteTarget(null)
    }
  }

  // Dialog controls
  const resetForm = () =>
    setFormData({
      name: "",
      features: [""],
      link: "",
      isActive: true
    })

  const closeDialog = () => {
    resetForm()
    setEditingGateway(null)
    setIsDialogOpen(false)
  }

  const openDialog = () => {
    resetForm()
    setEditingGateway(null)
    setIsDialogOpen(true)
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="w-full mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Payment Gateways</h1>
                <p className="text-gray-500 text-sm">Manage payment gateways and their features</p>
              </div>
            </div>

            <button
              onClick={openDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
            >
              <FiPlus className="text-lg" />
              Add Gateway
            </button>
          </div>
        </div>

        {/* Gateways Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.NO</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Features
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {gateways.length ? (
                gateways.map((gateway, idx) => (
                  <tr key={gateway._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{idx + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{gateway.name}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {gateway.features.map((feature, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${
                          gateway.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        }`}
                      >
                        {gateway.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <div className="flex items-center justify-center gap-3">
                        <button onClick={() => handleEdit(gateway)} className="text-blue-600 hover:text-blue-900">
                          <FiEdit className="h-5 w-5" />
                        </button>
                        <button onClick={() => confirmDelete(gateway._id)} className="text-red-600 hover:text-red-900">
                          <FiTrash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No gateways found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isDialogOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
          onClick={closeDialog}
        >
          <div
            className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {editingGateway ? "Edit Gateway" : "Add Gateway"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link (Optional)</label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Active
                </label>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                {formData.features.map((feat, i) => (
                  <div key={i} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={feat}
                      onChange={(e) => handleFeatureChange(i, e.target.value)}
                      placeholder={`Feature ${i + 1}`}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={i === 0}
                    />
                    {formData.features.length > 1 && (
                      <button type="button" onClick={() => removeFeature(i)} className="ml-2 text-red-500">
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addFeature} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
                  + Add Feature
                </button>
              </div>

              {/* Save / Cancel */}
              <div className="flex gap-2 pt-4">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md">
                  Save
                </button>
                <button
                  type="button"
                  onClick={closeDialog}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setDeleteTarget(null)}
        >
          <div className="bg-white rounded-lg w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Confirm Delete</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this gateway? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}